import React from "react";

/**
 * ErrorBoundary that catches ChunkLoadError (missing JS chunks after deploy)
 * and auto-reloads the page once to load fresh assets.
 *
 * After rsync --delete during deploy, old JS chunks are removed.
 * Users with cached pages still reference old chunk filenames,
 * causing "Loading chunk X failed" errors. This boundary catches
 * that and does a silent reload.
 */

interface State {
  hasError: boolean;
}

const RELOAD_KEY = "_chunk_reload";

function isChunkLoadError(error: Error): boolean {
  const msg = error.message || "";
  const name = error.name || "";
  return (
    name === "ChunkLoadError" ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    (msg.includes("Failed to load") && msg.includes(".js"))
  );
}

class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) {
      return { hasError: true };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      // Only auto-reload once to prevent infinite loops
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Fallback while reloading (shown briefly)
      return null;
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
